import { faCheck, faX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconLinkButton } from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import {
  Button,
  Pagination,
  Table,
  TableCell,
  TableContainer,
  TableFooter,
  TableHeader,
  TableRow,
} from '@windmill/react-ui';
import React, { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import {
  approveUserTeamRequest,
  denyUserTeamRequest,
} from '../../common/api/team';
import { addToast } from '../../common/reducers/toasterSlice';

interface UserRequestProps {
  user: Invitee;
  removeInvitation: (id: string) => void;
}

const UserRequest: React.FunctionComponent<UserRequestProps> = ({
  user,
  removeInvitation,
}): JSX.Element => {
  const dispatch = useDispatch();
  const { currentOrg } = useOrganization();
  const { currentTeam, refreshTeams } = useTeam();
  const approveRequest = async (user: Invitee) => {
    try {
      await approveUserTeamRequest(
        currentOrg?.id as string,
        currentTeam?.id as string,
        user.email
      );
      const newToastState = {
        message: `Team invitation request accepted. ${user.email} is now a member of ${currentTeam?.name}`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(newToastState));
      removeInvitation(user.id);
      refreshTeams();
    } catch (e) {
      const newToastState = {
        message: `There was an error accepting the invitation from ${user.email}`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(newToastState));
    }
  };

  const denyRequest = async (invitee: Invitee) => {
    try {
      await denyUserTeamRequest(
        currentOrg?.id as string,
        currentTeam?.id as string,
        invitee.email,
        invitee.id
      );
      const newToastState = {
        message: `Team invitation request denied. ${user.email} was not accepted to ${currentTeam?.name}`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(newToastState));
      removeInvitation(user.id);
    } catch {
      const newToastState = {
        message: `There was an error denying the invitation from ${user.email}`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(newToastState));
    }
  };

  return (
    <TableRow>
      <TableCell className="w-2/3">
        <span>{user.email}</span>
      </TableCell>
      <TableCell>
        <div className="flex flex-row justify-start gap-x-6">
          <Button
            className="h-10 w-28"
            onClick={() => approveRequest(user)}
            size="large"
          >
            <FontAwesomeIcon className="fa-lg text-white pr-2" icon={faCheck} />
            Accept
          </Button>
          <IconLinkButton
            icon={faX}
            iconClassName="fa-lg"
            linkText="Decline"
            onClick={() => denyRequest(user)}
          />
        </div>
      </TableCell>
    </TableRow>
  );
};

interface UserRequestListProps {
  pendingRequests: Invitee[];
  removeInvitation: (id: string) => void;
}

export const UserRequestList: FunctionComponent<UserRequestListProps> = ({
  pendingRequests,
  removeInvitation,
}): JSX.Element => {
  const [pageTable, setPageTable] = React.useState(1);
  const [dataTable, setDataTable] = React.useState<Invitee[]>([]);
  const resultsPerPage = 5;
  React.useEffect(() => {
    setDataTable(
      pendingRequests.slice(
        (pageTable - 1) * resultsPerPage,
        pageTable * resultsPerPage
      )
    );
  }, [pageTable, pendingRequests]);

  const onPageChange = (pageNumber: number) => {
    setPageTable(pageNumber);
  };

  const renderPendingRequestList = () => {
    return dataTable.map((user) => (
      <UserRequest
        key={user.id}
        removeInvitation={removeInvitation}
        user={user}
      />
    ));
  };

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHeader>
        {renderPendingRequestList()}
      </Table>
      <TableFooter>
        <Pagination
          label="Table navigation"
          onChange={onPageChange}
          resultsPerPage={resultsPerPage}
          totalResults={pendingRequests?.length}
        />
      </TableFooter>
    </TableContainer>
  );
};
